import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Project, Task, ProjectMemberProfile } from '../types';
import { ArrowLeft, Plus, Share2, CheckCircle, Circle, ChevronDown, ChevronUp, GripVertical, Users, UserPlus, Edit, Trash2, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { getCurrentWeekStart, getNextWeekStart, getWeekAfterNextStart, getWeekDays, formatDayDate, getDayName, getWeekStartString } from '../utils/weekUtils';
import ShareListModal from '../components/ShareListModal';
import { api } from '../utils/api';

type View = 'weekly' | 'planning' | 'settings';

interface TaskCardProps {
  task: Task;
  onToggle: (taskId: string, completed: boolean) => void;
  onDelete?: (taskId: string) => void;
  onContextMenu?: (e: React.MouseEvent, taskId: string) => void;
  onSchedule?: (taskId: string) => void;
  onAssign?: (taskId: string) => void;
  isDragging?: boolean;
  isSwiped?: boolean;
  onSwipe?: (taskId: string | null) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  onToggle, 
  onDelete, 
  onContextMenu, 
  onSchedule,
  onAssign,
  isDragging,
  isSwiped,
  onSwipe
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [translateX, setTranslateX] = useState<number>(0);
  const scheduleButtonRef = useRef<HTMLButtonElement>(null);
  const assignButtonRef = useRef<HTMLButtonElement>(null);
  
  // Calculate swipe width based on which buttons are available
  const hasSchedule = !!onSchedule;
  const hasAssign = !!onAssign;
  const buttonCount = (hasSchedule ? 1 : 0) + (hasAssign ? 1 : 0);
  const swipeWidth = buttonCount * 80; // 80px per button

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const currentTouchX = e.targetTouches[0].clientX;
      if (touchStart !== null) {
      const distance = touchStart - currentTouchX;
      if (distance > 0) {
        // Swiping left
        setTranslateX(Math.max(-swipeWidth, -Math.abs(distance)));
      } else {
        // Swiping right
        setTranslateX(Math.min(0, Math.abs(distance)));
      }
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) {
      setTouchStart(null);
      return;
    }
    
    // Check if touch ended on any button
    const touch = e.changedTouches[0];
    const finalTouchX = touch.clientX;
    
    const checkButton = (buttonRef: React.RefObject<HTMLButtonElement | null>) => {
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        return (
          finalTouchX >= buttonRect.left &&
          finalTouchX <= buttonRect.right &&
          touch.clientY >= buttonRect.top &&
          touch.clientY <= buttonRect.bottom
        );
      }
      return false;
    };
    
    if (checkButton(scheduleButtonRef) || checkButton(assignButtonRef)) {
      // Don't handle swipe if touching a button
      setTouchStart(null);
      return;
    }
    
    // Use the final touch position from the event, not from state
    const distance = touchStart - finalTouchX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setTranslateX(-swipeWidth);
      if (onSwipe) {
        onSwipe(task.id);
      }
    } else if (isRightSwipe) {
      setTranslateX(0);
      if (onSwipe) {
        onSwipe(null);
      }
    } else {
      setTranslateX(0);
    }

    setTouchStart(null);
  };

  // Reset translate when swiped state changes externally
  useEffect(() => {
    if (!isSwiped) {
      setTranslateX(0);
    } else {
      setTranslateX(-swipeWidth);
    }
  }, [isSwiped, swipeWidth]);

  return (
    <div className="relative overflow-hidden mb-2">
      {/* Action Buttons (revealed on swipe) */}
      <div className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-auto" style={{ width: `${swipeWidth}px` }}>
        {hasAssign && (
          <button
            ref={assignButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onAssign) {
                onAssign(task.id);
              }
              setTranslateX(0);
              if (onSwipe) {
                onSwipe(null);
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              setTouchStart(null);
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setTimeout(() => {
                if (onAssign) {
                  onAssign(task.id);
                }
                setTranslateX(0);
                if (onSwipe) {
                  onSwipe(null);
                }
              }, 0);
            }}
            className="text-white font-medium text-sm px-3 w-20 h-full flex items-center justify-center bg-green-600 touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            Assign
          </button>
        )}
        {hasSchedule && (
          <button
            ref={scheduleButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onSchedule) {
                onSchedule(task.id);
              }
              setTranslateX(0);
              if (onSwipe) {
                onSwipe(null);
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              setTouchStart(null);
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setTimeout(() => {
                if (onSchedule) {
                  onSchedule(task.id);
                }
                setTranslateX(0);
                if (onSwipe) {
                  onSwipe(null);
                }
              }, 0);
            }}
            className="text-white font-medium text-sm px-3 w-20 h-full flex items-center justify-center bg-blue-600 touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            Schedule
          </button>
        )}
      </div>

      {/* Task Card */}
      <div
        className={`bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-all ${
          isDragging ? 'opacity-50' : ''
        } ${task.completed ? 'opacity-60' : ''}`}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: touchStart === null ? 'transform 0.3s ease' : 'none',
          touchAction: 'pan-y',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onContextMenu={(e) => {
          e.preventDefault();
          if (onContextMenu) {
            onContextMenu(e, task.id);
          }
        }}
        onClick={(e) => {
          // Close swipe if clicking outside (but not on action buttons)
          if (isSwiped && onSwipe) {
            const target = e.target as HTMLElement;
            const isActionButton = target.closest('button')?.textContent === 'Schedule' || 
                                   target.closest('button')?.textContent === 'Assign';
            if (!isActionButton) {
              onSwipe(null);
            }
          }
        }}
      >
        <div className="flex items-start space-x-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(task.id, !task.completed);
            }}
            className="mt-0.5 flex-shrink-0"
          >
            {task.completed ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Circle className="w-5 h-5 text-gray-400 hover:text-green-600" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <h4 className={`text-sm font-medium text-gray-900 ${task.completed ? 'line-through' : ''}`}>
              {task.title}
            </h4>
            {task.description && (
              <p className="text-xs text-gray-500 mt-1">{task.description}</p>
            )}
            {task.assigned_to_member && (
              <div className="flex items-center mt-2 text-xs text-gray-500">
                <Users className="w-3 h-3 mr-1" />
                <span>{task.assigned_to_member.name}</span>
              </div>
            )}
          </div>
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
              className="text-gray-400 hover:text-red-600 transition-colors"
            >
              <span className="text-xs">×</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface SortableTaskCardProps extends TaskCardProps {
  id: string;
  isSwiped?: boolean;
  onSwipe?: (taskId: string | null) => void;
}

const SortableTaskCard: React.FC<SortableTaskCardProps> = ({ id, isSwiped, onSwipe, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center space-x-2">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1">
          <TaskCard {...props} isDragging={isDragging} isSwiped={isSwiped} onSwipe={onSwipe} />
        </div>
      </div>
    </div>
  );
};

interface DroppableDayProps {
  id: string;
  day: Date;
  dayIndex: number;
  tasks: Task[];
  onToggle: (taskId: string, completed: boolean) => void;
  onDelete: (taskId: string) => void;
  onContextMenu: (e: React.MouseEvent, taskId: string) => void;
  onSchedule?: (taskId: string) => void;
  onAssign?: (taskId: string) => void;
  swipedTaskId?: string | null;
  onSwipe?: (taskId: string | null) => void;
}

const DroppableDay: React.FC<DroppableDayProps> = ({
  id,
  day,
  dayIndex,
  tasks,
  onToggle,
  onDelete,
  onContextMenu,
  onSchedule,
  onAssign,
  swipedTaskId,
  onSwipe,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-xl border-2 border-gray-200 p-4 min-h-[200px] transition-colors ${
        isOver ? 'border-blue-400 bg-blue-50' : ''
      }`}
    >
      <h3 className="text-sm font-semibold text-gray-900 mb-3">
        {formatDayDate(day)}
      </h3>
      <div className="space-y-2">
        {tasks.map(task => (
          <TaskCard
            key={task.id}
            task={task}
            onToggle={onToggle}
            onDelete={onDelete}
            onContextMenu={onContextMenu}
            onSchedule={onSchedule}
            onAssign={onAssign}
            isSwiped={swipedTaskId === task.id}
            onSwipe={onSwipe}
          />
        ))}
        {tasks.length === 0 && (
          <p className="text-xs text-gray-400 py-4 text-center">
            Drop tasks here
          </p>
        )}
      </div>
    </div>
  );
};

interface DroppableWeekPoolProps {
  weekStart: string;
  tasks: Task[];
  onToggle: (taskId: string, completed: boolean) => void;
  onDelete: (taskId: string) => void;
  onContextMenu: (e: React.MouseEvent, taskId: string) => void;
  onSchedule?: (taskId: string) => void;
  onAssign?: (taskId: string) => void;
  swipedTaskId?: string | null;
  onSwipe?: (taskId: string | null) => void;
}

const DroppableWeekPool: React.FC<DroppableWeekPoolProps> = ({
  weekStart,
  tasks,
  onToggle,
  onDelete,
  onContextMenu,
  onSchedule,
  onAssign,
  swipedTaskId,
  onSwipe,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'week-pool',
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-blue-50 rounded-xl border-2 border-blue-300 p-4 transition-colors ${
        isOver ? 'border-blue-500 bg-blue-100' : ''
      }`}
    >
      <h3 className="text-sm font-semibold text-blue-700 mb-3">
        Week Pool ({weekStart})
      </h3>
      <div className="min-h-[100px]">
        <SortableContext
          items={tasks.map(t => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map(task => (
            <SortableTaskCard
              key={task.id}
              id={task.id}
              task={task}
              onToggle={onToggle}
              onDelete={onDelete}
              onContextMenu={onContextMenu}
              onSchedule={onSchedule}
              onAssign={onAssign}
              isSwiped={swipedTaskId === task.id}
              onSwipe={onSwipe}
            />
          ))}
        </SortableContext>
        {tasks.length === 0 && (
          <p className="text-xs text-gray-400 py-4 text-center">
            Drop tasks here
          </p>
        )}
      </div>
    </div>
  );
};

interface DroppableWeeklyDayProps {
  day: Date;
  dayIndex: number;
  tasks: Task[];
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onToggleTask: (taskId: string, completed: boolean) => void;
  onDeleteTask: (taskId: string) => void;
  onContextMenu: (e: React.MouseEvent, taskId: string) => void;
  onSchedule?: (taskId: string) => void;
  onAssign?: (taskId: string) => void;
  swipedTaskId?: string | null;
  onSwipe?: (taskId: string | null) => void;
}

const DroppableWeeklyDay: React.FC<DroppableWeeklyDayProps> = ({
  day,
  dayIndex,
  tasks,
  isExpanded,
  onToggleExpanded,
  onToggleTask,
  onDeleteTask,
  onContextMenu,
  onSchedule,
  onAssign,
  swipedTaskId,
  onSwipe,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dayIndex}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-xl shadow-sm border border-gray-200 transition-colors ${
        isOver ? 'border-blue-400 bg-blue-50' : ''
      }`}
    >
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <h3 className="text-base font-semibold text-gray-900">
            {formatDayDate(day)}
          </h3>
          {tasks.length > 0 && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {tasks.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-4">
          {tasks.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No tasks for this day</p>
          ) : (
            <SortableContext
              items={tasks.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              {tasks.map(task => (
                <SortableTaskCard
                  key={task.id}
                  id={task.id}
                  task={task}
                  onToggle={onToggleTask}
                  onDelete={onDeleteTask}
                  onContextMenu={onContextMenu}
                  onSchedule={onSchedule}
                  onAssign={onAssign}
                  isSwiped={swipedTaskId === task.id}
                  onSwipe={onSwipe}
                />
              ))}
            </SortableContext>
          )}
        </div>
      )}
    </div>
  );
};

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [memberProfiles, setMemberProfiles] = useState<ProjectMemberProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('weekly');
  const [currentWeekStart, setCurrentWeekStart] = useState<string>(getCurrentWeekStart());
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([0, 1, 2, 3, 4, 5, 6]));
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskDate, setNewTaskDate] = useState<string>('');
  const [newTaskWeek, setNewTaskWeek] = useState<string>(getCurrentWeekStart());
  const [newTaskWeekOption, setNewTaskWeekOption] = useState<string>('this-week');
  const [newTaskMember, setNewTaskMember] = useState<string>('');
  const [newMemberName, setNewMemberName] = useState('');
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [contextMenu, setContextMenu] = useState<{ taskId: string; x: number; y: number } | null>(null);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingMemberName, setEditingMemberName] = useState<string>('');
  const [swipedTaskId, setSwipedTaskId] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false);
  const [scheduleTaskId, setScheduleTaskId] = useState<string | null>(null);
  const [planningWeekOption, setPlanningWeekOption] = useState<string>('this-week');
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [assignTaskId, setAssignTaskId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  }, [projectId]);

  const fetchTasks = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}/tasks`);
      if (response.ok) {
        const data = await response.json();
        setTasks(data || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [projectId]);

  const fetchMemberProfiles = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}/member-profiles`);
      if (response.ok) {
        const data = await response.json();
        setMemberProfiles(data || []);
      }
    } catch (error) {
      console.error('Error fetching member profiles:', error);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId && user) {
      setLoading(true);
      Promise.all([fetchProject(), fetchTasks(), fetchMemberProfiles()]).finally(() => {
        setLoading(false);
      });
    }
  }, [projectId, user, fetchProject, fetchTasks, fetchMemberProfiles]);

  // Sync planning week option when switching to planning view
  useEffect(() => {
    if (view === 'planning') {
      const currentWeek = getCurrentWeekStart();
      const nextWeek = getNextWeekStart();
      const weekAfter = getWeekAfterNextStart();
      
      if (currentWeekStart === currentWeek) {
        setPlanningWeekOption('this-week');
      } else if (currentWeekStart === nextWeek) {
        setPlanningWeekOption('next-week');
      } else if (currentWeekStart === weekAfter) {
        setPlanningWeekOption('week-after');
      } else {
        // If it's a different week, default to this week
        setPlanningWeekOption('this-week');
        setCurrentWeekStart(currentWeek);
      }
    }
  }, [view, currentWeekStart]);

  const createTask = async () => {
    if (!newTaskTitle.trim() || !projectId || !user) return;

    // Ensure week_start_date is always set
    let weekStartDate = newTaskWeek;
    if (!weekStartDate) {
      // Default to current week if no week is selected
      weekStartDate = getCurrentWeekStart();
    }

    // Calculate day_of_week from date if date is set
    let dayOfWeek: number | null = null;
    if (newTaskDate) {
      // Parse date string as local date to avoid timezone issues
      // Date input returns "YYYY-MM-DD" format, parse it as local time
      const [year, month, day] = newTaskDate.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      const dayOfWeekValue = d.getDay();
      dayOfWeek = dayOfWeekValue === 0 ? 6 : dayOfWeekValue - 1; // Convert Sunday=0 to Sunday=6, others shift by 1
    }

    try {
      const response = await api.post(`/projects/${projectId}/tasks`, {
        title: newTaskTitle.trim(),
        description: newTaskDescription.trim() || null,
        week_start_date: weekStartDate,
        day_of_week: dayOfWeek,
        assigned_to_member_id: newTaskMember || null,
      });

      if (response.ok) {
        const newTask = await response.json();
        setTasks([...tasks, newTask]);
        setNewTaskTitle('');
        setNewTaskDescription('');
        setNewTaskDate('');
        setNewTaskWeek(getCurrentWeekStart());
        setNewTaskWeekOption('this-week');
        setNewTaskMember('');
        setShowAddTaskForm(false);
      }
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const toggleTask = async (taskId: string, completed: boolean) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, { completed });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      const response = await api.delete(`/tasks/${taskId}`);

      if (response.ok) {
        setTasks(tasks.filter(t => t.id !== taskId));
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const createMemberProfile = async () => {
    if (!newMemberName.trim() || !projectId || !user) return;

    try {
      const response = await api.post(`/projects/${projectId}/member-profiles`, {
        name: newMemberName.trim(),
      });

      if (response.ok) {
        const newMember = await response.json();
        setMemberProfiles([...memberProfiles, newMember]);
        setNewMemberName('');
        setShowAddMemberForm(false);
      }
    } catch (error) {
      console.error('Error creating member profile:', error);
    }
  };

  const updateMemberProfile = async (profileId: string, name: string) => {
    if (!name.trim() || !projectId) return;

    try {
      const response = await api.patch(`/projects/${projectId}/member-profiles/${profileId}`, {
        name: name.trim(),
      });

      if (response.ok) {
        const updatedMember = await response.json();
        setMemberProfiles(memberProfiles.map(m => m.id === profileId ? updatedMember : m));
        setEditingMemberId(null);
        setEditingMemberName('');
      }
    } catch (error) {
      console.error('Error updating member profile:', error);
    }
  };

  const deleteMemberProfile = async (profileId: string) => {
    if (!projectId) return;

    if (!window.confirm('Are you sure you want to delete this family member? This will also remove them from any assigned tasks.')) {
      return;
    }

    try {
      const response = await api.delete(`/projects/${projectId}/member-profiles/${profileId}`);

      if (response.ok) {
        setMemberProfiles(memberProfiles.filter(m => m.id !== profileId));
        setEditingMemberId(null);
        setEditingMemberName('');
      }
    } catch (error) {
      console.error('Error deleting member profile:', error);
    }
  };

  const startEditingMember = (member: ProjectMemberProfile) => {
    setEditingMemberId(member.id);
    setEditingMemberName(member.name);
  };

  const cancelEditingMember = () => {
    setEditingMemberId(null);
    setEditingMemberName('');
  };

  const shareProject = async (email: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await api.post(`/projects/${projectId}/share`, {
        email,
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        throw new Error('Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sharing project:', error);
      throw error;
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    setDraggedTask(task || null);
  };

  const moveTaskToWeek = async (taskId: string, weekStartDate: string) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, {
        week_start_date: weekStartDate,
        day_of_week: null, // Assign to week pool, not a specific day
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      }
    } catch (error) {
      console.error('Error moving task to week:', error);
    }
  };

  const scheduleTaskToDay = async (taskId: string, weekStartDate: string, dayIndex: number) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, {
        week_start_date: weekStartDate,
        day_of_week: dayIndex,
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      }
    } catch (error) {
      console.error('Error scheduling task to day:', error);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, taskId: string) => {
    e.preventDefault();
    setContextMenu({
      taskId,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleScheduleClick = (taskId: string) => {
    setScheduleTaskId(taskId);
    setShowScheduleModal(true);
    setSwipedTaskId(null);
  };

  const handleAssignClick = (taskId: string) => {
    setAssignTaskId(taskId);
    setShowAssignModal(true);
    setSwipedTaskId(null);
  };

  const assignTaskToMember = async (taskId: string, memberId: string | null) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, {
        assigned_to_member_id: memberId,
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
        setShowAssignModal(false);
        setAssignTaskId(null);
      }
    } catch (error) {
      console.error('Error assigning task:', error);
    }
  };

  const handleSwipe = (taskId: string | null) => {
    setSwipedTaskId(taskId);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setDraggedTask(null);

    const { active, over } = event;
    if (!over || !active.id) return;

    const task = tasks.find(t => t.id === active.id);
    if (!task) return;

    // Check if dropped on a week panel (this week, next week, week after)
    const weekMatch = over.id.toString().match(/^week-(this|next|after)$/);
    if (weekMatch) {
      const weekType = weekMatch[1];
      let weekStart: string;
      
      if (weekType === 'this') {
        weekStart = getCurrentWeekStart();
      } else if (weekType === 'next') {
        weekStart = getNextWeekStart();
      } else {
        weekStart = getWeekAfterNextStart();
      }

      await moveTaskToWeek(task.id, weekStart);
      return;
    }

    // Check if dropped on a day
    const dayMatch = over.id.toString().match(/^day-(\d+)$/);
    if (dayMatch) {
      const dayIndex = parseInt(dayMatch[1]);
      const weekStart = currentWeekStart;

      try {
        const response = await api.patch(`/tasks/${task.id}`, {
          week_start_date: weekStart,
          day_of_week: dayIndex,
        });

        if (response.ok) {
          const updatedTask = await response.json();
          setTasks(tasks.map(t => t.id === task.id ? updatedTask : t));
        }
      } catch (error) {
        console.error('Error updating task:', error);
      }
      return;
    }

    // Check if dropped in week pool
    if (over.id === 'week-pool') {
      await moveTaskToWeek(task.id, currentWeekStart);
      return;
    }

  };

  const toggleDayExpanded = (dayIndex: number) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dayIndex)) {
      newExpanded.delete(dayIndex);
    } else {
      newExpanded.add(dayIndex);
    }
    setExpandedDays(newExpanded);
  };

  const getTasksForDay = (dayIndex: number): Task[] => {
    return tasks.filter(
      t => t.week_start_date === currentWeekStart && 
      t.day_of_week === dayIndex &&
      !t.completed
    );
  };

  const getWeekPoolTasks = (): Task[] => {
    return tasks.filter(
      t => t.week_start_date === currentWeekStart && 
      t.day_of_week === null
    );
  };

  const weekDays = getWeekDays(currentWeekStart);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Project not found</p>
          <button
            onClick={() => navigate('/tasks')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/tasks')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </button>
            </div>
          </div>

          {/* View Tabs */}
          <div className="flex space-x-2 border-b border-gray-200">
            <button
              onClick={() => setView('weekly')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                view === 'weekly'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => setView('planning')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                view === 'planning'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Plan Week
            </button>
            <button
              onClick={() => setView('settings')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                view === 'settings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Weekly View */}
        {view === 'weekly' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">This Week</h2>
              <button
                onClick={() => setShowAddTaskForm(true)}
                className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </button>
            </div>

            {weekDays.map((day, dayIndex) => {
              const dayTasks = getTasksForDay(dayIndex);
              const isExpanded = expandedDays.has(dayIndex);

              return (
                <DroppableWeeklyDay
                  key={dayIndex}
                  day={day}
                  dayIndex={dayIndex}
                  tasks={dayTasks}
                  isExpanded={isExpanded}
                  onToggleExpanded={() => toggleDayExpanded(dayIndex)}
                  onToggleTask={toggleTask}
                  onDeleteTask={deleteTask}
                  onContextMenu={handleContextMenu}
                  onSchedule={handleScheduleClick}
                  onAssign={handleAssignClick}
                  swipedTaskId={swipedTaskId}
                  onSwipe={handleSwipe}
                />
              );
            })}
          </div>
        )}

        {/* Planning View */}
        {view === 'planning' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Plan Week</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const weekStart = getCurrentWeekStart();
                    setPlanningWeekOption('this-week');
                    setCurrentWeekStart(weekStart);
                  }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    planningWeekOption === 'this-week'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  This Week
                </button>
                <button
                  onClick={() => {
                    const weekStart = getNextWeekStart();
                    setPlanningWeekOption('next-week');
                    setCurrentWeekStart(weekStart);
                  }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    planningWeekOption === 'next-week'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Next Week
                </button>
                <button
                  onClick={() => {
                    const weekStart = getWeekAfterNextStart();
                    setPlanningWeekOption('week-after');
                    setCurrentWeekStart(weekStart);
                  }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    planningWeekOption === 'week-after'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Week After
                </button>
              </div>
            </div>

            {/* Week Pool */}
            <DroppableWeekPool
              weekStart={currentWeekStart}
              tasks={getWeekPoolTasks()}
              onToggle={toggleTask}
              onDelete={deleteTask}
              onContextMenu={handleContextMenu}
              onSchedule={handleScheduleClick}
              onAssign={handleAssignClick}
              swipedTaskId={swipedTaskId}
              onSwipe={handleSwipe}
            />

            {/* Days */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {weekDays.map((day, dayIndex) => {
                const dayTasks = tasks.filter(
                  t => t.week_start_date === currentWeekStart && t.day_of_week === dayIndex
                );

                return (
                  <DroppableDay
                    key={dayIndex}
                    id={`day-${dayIndex}`}
                    day={day}
                    dayIndex={dayIndex}
                    tasks={dayTasks}
                    onToggle={toggleTask}
                    onDelete={deleteTask}
                    onContextMenu={handleContextMenu}
                    onSchedule={handleScheduleClick}
                    onAssign={handleAssignClick}
                    swipedTaskId={swipedTaskId}
                    onSwipe={handleSwipe}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Settings View */}
        {view === 'settings' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
            </div>

            {/* Manage Family Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Manage Family</h3>
                  <p className="text-sm text-gray-600">Add, edit, or remove family members</p>
                </div>
                <button
                  onClick={() => setShowAddMemberForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </button>
              </div>

              {memberProfiles.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-base font-medium text-gray-900 mb-1">No family members yet</h4>
                  <p className="text-sm text-gray-600 mb-4">Add your first family member to get started</p>
                  <button
                    onClick={() => setShowAddMemberForm(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add Member
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {memberProfiles.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      {editingMemberId === member.id ? (
                        <div className="flex items-center space-x-3 flex-1">
                          <input
                            type="text"
                            value={editingMemberName}
                            onChange={(e) => setEditingMemberName(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateMemberProfile(member.id, editingMemberName);
                              } else if (e.key === 'Escape') {
                                cancelEditingMember();
                              }
                            }}
                          />
                          <button
                            onClick={() => updateMemberProfile(member.id, editingMemberName)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Save"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={cancelEditingMember}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center space-x-3 flex-1">
                            <Users className="w-5 h-5 text-gray-400" />
                            <span className="text-base font-medium text-gray-900">{member.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => startEditingMember(member)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteMemberProfile(member.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Task Form Modal */}
        {showAddTaskForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Create Task</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder="e.g., Buy groceries"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    placeholder="Optional description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Week
                  </label>
                  <select
                    value={newTaskWeekOption}
                    onChange={(e) => {
                      const option = e.target.value;
                      setNewTaskWeekOption(option);
                      if (option && option !== 'custom') {
                        let weekStart = '';
                        if (option === 'this-week') {
                          weekStart = getCurrentWeekStart();
                        } else if (option === 'next-week') {
                          weekStart = getNextWeekStart();
                        } else if (option === 'week-after') {
                          weekStart = getWeekAfterNextStart();
                        }
                        setNewTaskWeek(weekStart);
                        // Clear date when week is changed
                        if (newTaskDate) {
                          setNewTaskDate('');
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="this-week">This Week</option>
                    <option value="next-week">Next Week</option>
                    <option value="week-after">Week After</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date (optional)
                  </label>
                  <input
                    type="date"
                    value={newTaskDate || ''}
                    placeholder=""
                    onChange={(e) => {
                      const date = e.target.value;
                      setNewTaskDate(date || '');
                      if (date) {
                        // Calculate week start (Monday) and update week field
                        const d = new Date(date);
                        const weekStart = getWeekStartString(d);
                        setNewTaskWeek(weekStart);
                        
                        // Update week option to match the selected date's week
                        const currentWeek = getCurrentWeekStart();
                        const nextWeek = getNextWeekStart();
                        const weekAfter = getWeekAfterNextStart();
                        
                        if (weekStart === currentWeek) {
                          setNewTaskWeekOption('this-week');
                        } else if (weekStart === nextWeek) {
                          setNewTaskWeekOption('next-week');
                        } else if (weekStart === weekAfter) {
                          setNewTaskWeekOption('week-after');
                        } else {
                          // If it's a different week, set to custom
                          setNewTaskWeekOption('custom');
                        }
                      }
                      // If date is cleared, leave it empty - don't modify week field
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to Member (optional)
                  </label>
                  <select
                    value={newTaskMember}
                    onChange={(e) => setNewTaskMember(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No assignment</option>
                    {memberProfiles.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={createTask}
                  disabled={!newTaskTitle.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowAddTaskForm(false);
                    setNewTaskTitle('');
                    setNewTaskDescription('');
                    setNewTaskDate('');
                    setNewTaskWeek(getCurrentWeekStart());
                    setNewTaskWeekOption('this-week');
                    setNewTaskMember('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Member Form Modal */}
        {showAddMemberForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Add Member</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newMemberName}
                    onChange={(e) => setNewMemberName(e.target.value)}
                    placeholder="e.g., John, Mom, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={createMemberProfile}
                  disabled={!newMemberName.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddMemberForm(false);
                    setNewMemberName('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && project && (
          <ShareListModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            onShare={shareProject}
            listName={project.name}
            listId={project.id}
            currentUserId={user?.id || ''}
          />
        )}

        {/* Drag Overlay */}
        <DragOverlay>
          {draggedTask && (
            <div className="bg-white rounded-lg border-2 border-blue-500 p-3 shadow-lg opacity-90">
              <div className="flex items-center space-x-3">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{draggedTask.title}</h4>
                </div>
              </div>
            </div>
          )}
        </DragOverlay>

        {/* Context Menu */}
        {contextMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setContextMenu(null)}
            />
            <div
              className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] max-h-[400px] overflow-y-auto"
              style={{
                left: `${contextMenu.x}px`,
                top: `${contextMenu.y}px`,
              }}
            >
              {view === 'planning' ? (
                <>
                  <div className="text-xs font-semibold text-gray-500 px-4 py-2 border-b border-gray-200">
                    Schedule to Day
                  </div>
                  {weekDays.map((day, dayIndex) => (
                    <button
                      key={dayIndex}
                      onClick={() => {
                        scheduleTaskToDay(contextMenu.taskId, currentWeekStart, dayIndex);
                        setContextMenu(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {getDayName(dayIndex)}
                    </button>
                  ))}
                  <div className="border-t border-gray-200 my-1" />
                  <button
                    onClick={() => {
                      moveTaskToWeek(contextMenu.taskId, currentWeekStart);
                      setContextMenu(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                  >
                    Move to Week Pool
                  </button>
                </>
              ) : null}
            </div>
          </>
        )}

        {/* Schedule Day Selection Modal (Mobile-friendly) */}
        {showScheduleModal && scheduleTaskId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center">
            <div className="bg-white w-full sm:w-96 sm:rounded-t-2xl rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Schedule Task</h2>
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleTaskId(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Day Options */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-2">
                  {weekDays.map((day, dayIndex) => (
                    <button
                      key={dayIndex}
                      onClick={() => {
                        scheduleTaskToDay(scheduleTaskId, currentWeekStart, dayIndex);
                        setShowScheduleModal(false);
                        setScheduleTaskId(null);
                      }}
                      className="w-full text-left px-4 py-4 text-base text-gray-900 hover:bg-blue-50 active:bg-blue-100 border-b border-gray-100 last:border-b-0 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">{getDayName(dayIndex)}</div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {formatDayDate(day)}
                        </div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400 rotate-[-90deg]" />
                    </button>
                  ))}
                  <div className="border-t border-gray-200 my-2" />
                  <button
                    onClick={() => {
                      moveTaskToWeek(scheduleTaskId, currentWeekStart);
                      setShowScheduleModal(false);
                      setScheduleTaskId(null);
                    }}
                    className="w-full text-left px-4 py-4 text-base text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium">Week Pool</div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        Assign to week without specific day
                      </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-gray-400 rotate-[-90deg]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assign Member Modal (Mobile-friendly) */}
        {showAssignModal && assignTaskId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center">
            <div className="bg-white w-full sm:w-96 sm:rounded-t-2xl rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Assign Task</h2>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignTaskId(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Member Options */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-2">
                  <button
                    onClick={() => {
                      assignTaskToMember(assignTaskId, null);
                    }}
                    className="w-full text-left px-4 py-4 text-base text-gray-900 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 last:border-b-0 transition-colors flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium">No Assignment</div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        Unassign from any member
                      </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-gray-400 rotate-[-90deg]" />
                  </button>
                  {memberProfiles.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => {
                        assignTaskToMember(assignTaskId, member.id);
                      }}
                      className="w-full text-left px-4 py-4 text-base text-gray-900 hover:bg-blue-50 active:bg-blue-100 border-b border-gray-100 last:border-b-0 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">{member.name}</div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400 rotate-[-90deg]" />
                    </button>
                  ))}
                  {memberProfiles.length === 0 && (
                    <div className="px-4 py-8 text-center">
                      <Users className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600">No members available</p>
                      <p className="text-xs text-gray-500 mt-1">Add members in Settings</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
};

export default ProjectDetail;

